/**
 * The village plan in one sentence — the SAME sentence on the plan summary rail (Manual Control)
 * and on every Suggestion Engine card, so the two surfaces cannot describe a plan differently.
 *
 * Built ONLY from figures already on the screen beside it, subtracted exactly as the tiles subtract
 * them. The village difference from Aged Care is what leads: the entry contribution has to be paid
 * in full and its FUNDING is the decision, so where the money comes from leads where the DAP leads
 * there. Everything is measured against STAYING PUT — the only honest benchmark for an elective
 * move (owner, 24 Sep 2026).
 *
 * TWO THINGS IT MUST NEVER LEAVE OUT (owner, 24 Sep 2026):
 *   - a sale that does not cover the contribution: "paid in full from selling the investment
 *     property" on $651,966 of proceeds against an $800,000 contribution was simply false. The
 *     sentence now says what the sale paid and what savings paid.
 *   - a lifetime annuity with nothing returned on death: the estate is worse off BECAUSE the
 *     capital is gone, and a summary that blamed growth and fees while never mentioning it ("a
 *     load of bullox … you're going to get me sued if a client pleads ignorance") is not a summary.
 */
import React from 'react';
import { formatCurrency } from '../../utils/index.ts';
import {
    pensionClause,
    cashflowClause,
    estateClause,
    plainClause,
    RecapClauses,
    type RecapClause,
} from '../../components/strategy/recapClauses.tsx';

export interface RvInBriefAnnuity {
    amount: number;
    /** 0 = nothing returned on death; 100 = the capital comes back. */
    residualPercent: number;
    /** Whose life the payments are for — "Cameron", or "you". */
    ownerName: string;
}

export interface RvInBriefInput {
    entryFee: number;
    /** 'liquid' | 'sell_home' | 'investment_property' | 'rent_home' — how the entry contribution is paid. */
    fundFrom: string;
    rentHome?: boolean;
    /** On a property route: what the sale paid toward the contribution, and what savings paid. */
    fromSaleAmount?: number | null;
    fromSavingsAmount?: number | null;
    pensionBefore: number;
    pensionAfter: number;
    cashBefore: number;
    cashAfter: number;
    /** The plan's estate at the horizon, and staying put's — null when either could not be computed. */
    planLegacy: number | null;
    baseLegacy: number | null;
    legacyYears: number;
    /**
     * How much of the entry contribution the chosen funding does NOT cover. Above zero this is not
     * yet a move that can go ahead, and the sentence says so FIRST rather than narrating consequences
     * that assume the money appeared (owner-caught, 2026-08-27).
     */
    shortfall?: number;
    /** A lifetime annuity the plan buys, if any. */
    annuity?: RvInBriefAnnuity | null;
}

const SOURCE: Record<string, string> = {
    sell_home: 'selling the family home',
    investment_property: 'selling the investment property',
};

/** The plain-words fact of a nil-residual annuity, used by the sentence, the card and the recap. */
export function nilResidualAnnuityText(a: RvInBriefAnnuity): string {
    return `the ${formatCurrency(a.amount)} lifetime annuity returns nothing when ${a.ownerName} ${a.ownerName === 'you' ? 'die' : 'dies'} — that money leaves the estate the day it is bought`;
}

export function buildRvInBriefClauses(i: RvInBriefInput): RecapClause[] {
    const shortfall = Math.round(i.shortfall ?? 0);
    if (shortfall > 0) {
        return [{
            tone: 'bad',
            text: `${formatCurrency(shortfall)} of the ${formatCurrency(i.entryFee)} entry contribution still has to come `
                + `from somewhere — a village contribution has to be paid in full, so this is not yet a move that can go ahead. `
                + `The figures below assume it is funded`,
        }];
    }
    const clauses: RecapClause[] = [];
    if (i.entryFee > 0) {
        const source = SOURCE[i.fundFrom];
        const fromSale = Math.round(i.fromSaleAmount ?? 0);
        const fromSavings = Math.round(i.fromSavingsAmount ?? 0);
        const kept = i.rentHome ? ', with the family home kept and rented out' : '';
        if (source && fromSavings > 0) {
            clauses.push(plainClause(
                `the ${formatCurrency(i.entryFee)} entry contribution is paid in full: ${formatCurrency(fromSale)} from ${source} and ${formatCurrency(fromSavings)} from savings${kept}`,
            ));
        } else {
            clauses.push(plainClause(`the ${formatCurrency(i.entryFee)} entry contribution is paid in full from ${source ?? 'savings'}${kept}`));
        }
    }
    if (i.annuity && i.annuity.amount > 0 && Math.round(i.annuity.residualPercent) === 0) {
        clauses.push({ tone: 'bad', text: nilResidualAnnuityText(i.annuity) });
    }
    clauses.push(pensionClause(i.pensionAfter - i.pensionBefore));
    const cashflowDelta = i.cashAfter - i.cashBefore;
    if (Math.round(cashflowDelta) !== 0) clauses.push(cashflowClause(cashflowDelta, i.cashAfter));
    if (i.planLegacy != null && i.baseLegacy != null) {
        const legacyDelta = i.planLegacy - i.baseLegacy;
        // "ahead of staying put" — this module's baseline is named, which is clearer than "ahead".
        const e = estateClause(legacyDelta, Number(i.legacyYears) || 0);
        clauses.push(Math.round(legacyDelta) === 0 ? e : { ...e, text: e.text.replace(' ahead after', ' ahead of staying put after').replace(' behind after', ' behind staying put after') });
    }
    return clauses;
}

export function RvInBrief(props: RvInBriefInput) {
    const clauses = buildRvInBriefClauses(props);
    if (!clauses.length) return null;
    return <RecapClauses clauses={clauses} />;
}
